import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTINGAPI_URL, ACCOUNTING_API } from '../../../../config/api';
import type { InventoryItem, BranchPrice, CreateBranchPriceDto, UpdateBranchPriceDto, PriceChangeLogResponse } from '../types/inventory.types';
import { toLocalIso } from '../../../../utils/dateUtils';

// ============== SQL API DTO Types ==============

// ✅ Gelişmiş filtre seçenekleri (Ürün ve Stok listesi)
// getPagedItemsSQL ve loadInventoryItemsPaged için tek opsiyon nesnesi olarak taşınır;
// böylece pozisyonel argüman listesi şişmez ve mevcut çağrı yerleri bozulmaz.
export interface InventoryAdvancedFilters {
  hideZeroStock?: boolean;
  hidePositiveStock?: boolean;
  branchId?: string | null;
  locationId?: string | null;
  minSalePrice?: number | null;
  maxSalePrice?: number | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  categoryIds?: string[] | null;
  brandIds?: string[] | null;
  onlyWithoutImage?: boolean;
}

/** Canlı API henüz bu alanları desteklemiyorsa FE tarafında uygulanacak gelişmiş filtreler */
const hasClientSideAdvancedFilters = (advanced?: InventoryAdvancedFilters): boolean => {
  if (!advanced) return false;
  return (
    advanced.hideZeroStock === true ||
    advanced.hidePositiveStock === true ||
    !!advanced.branchId ||
    !!advanced.locationId ||
    advanced.minSalePrice != null ||
    advanced.maxSalePrice != null ||
    advanced.minQuantity != null ||
    advanced.maxQuantity != null ||
    !!advanced.createdFrom ||
    !!advanced.createdTo ||
    advanced.onlyWithoutImage === true ||
    !!(advanced.categoryIds && advanced.categoryIds.length > 0) ||
    !!(advanced.brandIds && advanced.brandIds.length > 0)
  );
};

const getItemQuantity = (item: any, warehouseId?: string | null): number => {
  const stocks = item.warehouseStocks ?? item.WarehouseStocks ?? [];
  if (!Array.isArray(stocks) || stocks.length === 0) {
    return Number(item.totalQuantity ?? item.TotalQuantity ?? item.quantity ?? item.Quantity ?? 0) || 0;
  }
  if (warehouseId) {
    return stocks
      .filter((ws: any) => String(ws.warehouseId ?? ws.WarehouseId) === String(warehouseId))
      .reduce((sum: number, ws: any) => sum + (Number(ws.quantity ?? ws.Quantity) || 0), 0);
  }
  return stocks.reduce((sum: number, ws: any) => sum + (Number(ws.quantity ?? ws.Quantity) || 0), 0);
};

const itemHasImage = (item: any): boolean => {
  const url = String(item.imageUrl ?? item.ImageUrl ?? '').trim();
  if (url) return true;
  const urls = item.imageUrls ?? item.ImageUrls;
  if (!urls) return false;
  if (Array.isArray(urls)) return urls.some((u) => String(u || '').trim());
  const s = String(urls).trim();
  if (!s || s === '[]' || s === 'null') return false;
  return true;
};

/** Gelişmiş filtreleri istemci tarafında uygula (canlı API geriye uyumluluk) */
const applyAdvancedFiltersClientSide = (
  items: any[],
  advanced: InventoryAdvancedFilters,
  warehouseId?: string | null
): any[] => {
  return items.filter((item) => {
    const salePrice = Number(item.salePrice ?? item.SalePrice ?? 0) || 0;
    const qty = getItemQuantity(item, warehouseId);
    const createdAtRaw = item.createdAt ?? item.CreatedAt;
    const createdAtMs = createdAtRaw ? new Date(createdAtRaw).getTime() : NaN;
    const branchesId = String(item.branchesId ?? item.BranchesId ?? '');

    if (advanced.minSalePrice != null && salePrice < advanced.minSalePrice) return false;
    if (advanced.maxSalePrice != null && salePrice > advanced.maxSalePrice) return false;
    if (advanced.minQuantity != null && qty < advanced.minQuantity) return false;
    if (advanced.maxQuantity != null && qty > advanced.maxQuantity) return false;

    if (advanced.hideZeroStock === true && qty <= 0) return false;
    if (advanced.hidePositiveStock === true && qty > 0) return false;

    if (advanced.onlyWithoutImage === true && itemHasImage(item)) return false;

    if (advanced.branchId) {
      // BranchesId tek id veya virgüllü liste olabilir
      const ids = branchesId.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
      if (!ids.includes(String(advanced.branchId)) && branchesId !== String(advanced.branchId)) {
        // contains fallback (eski veri)
        if (!branchesId.includes(String(advanced.branchId))) return false;
      }
    }

    if (advanced.locationId) {
      const stocks = item.warehouseStocks ?? item.WarehouseStocks ?? [];
      const hasLoc = Array.isArray(stocks) && stocks.some((ws: any) =>
        String(ws.locationId ?? ws.LocationId ?? '') === String(advanced.locationId)
      );
      if (!hasLoc) return false;
    }

    if (advanced.createdFrom) {
      const from = new Date(advanced.createdFrom).getTime();
      if (!Number.isFinite(createdAtMs) || createdAtMs < from) return false;
    }
    if (advanced.createdTo) {
      const to = new Date(advanced.createdTo).getTime();
      if (!Number.isFinite(createdAtMs) || createdAtMs > to) return false;
    }

    if (advanced.brandIds && advanced.brandIds.length > 0) {
      const bid = String(item.brandId ?? item.BrandId ?? '');
      if (!advanced.brandIds.map(String).includes(bid)) return false;
    }
    if (advanced.categoryIds && advanced.categoryIds.length > 0) {
      const cid = String(item.categoryId ?? item.CategoryId ?? '');
      if (!advanced.categoryIds.map(String).includes(cid)) return false;
    }

    return true;
  });
};

// ✅ YENİ: Depo stok oluşturma DTO'su
export interface CreateWarehouseStockDto {
  companyId: string;
  productId: string;
  warehouseId: string;
  quantity: number;
  locationId?: string;
}

export interface CreateInventoryItemDto {
  companyId: string;
  stockCode: string;
  name: string;
  categoryId?: string | null;
  brandId?: string | null;
  // ❌ KALDIRILDI: warehouseId ve quantity artık CreateWarehouseStockDto ile ayrı gönderilecek
  // warehouseId?: string | null;
  // quantity: number;
  
  // Alış Fiyatları
  purchasePrice?: number | null;
  purchaseCurrency?: string;
  purchaseVendor?: string;
  purchasePrice2?: number | null;
  purchaseCurrency2?: string;
  purchaseVendor2?: string;
  purchasePrice3?: number | null;
  purchaseCurrency3?: string;
  purchaseVendor3?: string;
  // Satış Fiyatları
  salePrice: number;
  saleCurrency: string;
  saleCustomer?: string;
  salePrice2?: number | null;
  saleCurrency2?: string;
  saleCustomer2?: string;
  salePrice3?: number | null;
  saleCurrency3?: string;
  saleCustomer3?: string;
  listPrice?: number | null;
  listCurrency?: string;
  // Stok
  unit: string;
  canBeNegative: boolean;
  minStockLevel?: number | null;
  maxStockLevel?: number | null;
  description?: string;
  barcode?: string;
  isDomesticProduct?: boolean;
  /** Ürün tartıya PLU olarak gönderilecek mi */
  isScale?: boolean;
  /** Ağırlık barkodunun ortasındaki 5 hane; baştaki sıfırlar anlamlı */
  scaleCode?: string;
  /**
   * Raf ömrü (gün). Tartı etikete son tüketim tarihini kendisi hesaplıyor:
   * tartım tarihi + gün - 1. Takvim tarihi değil, gün sayısı gönderiliyor.
   */
  shelfLifeDays?: number;
  countryOfOrigin?: string; // ISO 3166-1 alpha-2
  desi?: number | null;
  vatRate: number;
  vatStatus: string;
  sctRate?: number | null;
  sctStatus?: string;
  imageUrl?: string;
  imageUrls?: string[];
  variants?: any;
  branchesId?: string;
  parentItemId?: string | null;
  isVariantParent?: boolean;
  printerGroup?: string;
  departmentNo?: number | null;
  itemType?: string;
  isActive: boolean;
  isEcommerce?: boolean;
}

export interface UpdateInventoryItemDto {
  id: string;
  companyId: string;
  stockCode: string;
  name: string;
  categoryId?: string | null;
  brandId?: string | null;
  // ❌ KALDIRILDI: warehouseId ve quantity artık UpdateWarehouseStockDto ile ayrı güncellenir
  // warehouseId?: string | null;
  // quantity: number;
  branchesId?: string | null;
  // Fiyatlar
  purchasePrice?: number;
  purchaseCurrency?: string;
  purchaseVendor?: string;
  purchasePrice2?: number;
  purchaseCurrency2?: string;
  purchaseVendor2?: string;
  purchasePrice3?: number;
  purchaseCurrency3?: string;
  purchaseVendor3?: string;
  salePrice: number;
  saleCurrency: string;
  saleCustomer?: string;
  salePrice2?: number;
  saleCurrency2?: string;
  saleCustomer2?: string;
  salePrice3?: number;
  saleCurrency3?: string;
  saleCustomer3?: string;
  listPrice?: number;
  listCurrency?: string;
  // Stok
  unit: string;
  canBeNegative: boolean;
  minStockLevel?: number;
  maxStockLevel?: number;
  description?: string;
  barcode?: string;
  isDomesticProduct?: boolean;
  /** Ürün tartıya PLU olarak gönderilecek mi */
  isScale?: boolean;
  /** Ağırlık barkodunun ortasındaki 5 hane; baştaki sıfırlar anlamlı */
  scaleCode?: string;
  /**
   * Raf ömrü (gün). Tartı etikete son tüketim tarihini kendisi hesaplıyor:
   * tartım tarihi + gün - 1. Takvim tarihi değil, gün sayısı gönderiliyor.
   */
  shelfLifeDays?: number;
  countryOfOrigin?: string; // ISO 3166-1 alpha-2
  desi?: number | null;
  vatRate: number;
  vatStatus: string;
  sctRate?: number;
  sctStatus?: string;
  imageUrl?: string;
  imageUrls?: string[];
  variants?: any; // Backend kullanmıyor
  parentItemId?: string | null;
  isVariantParent?: boolean;
  printerGroup?: string;
  departmentNo?: number;
  itemType?: string;
  isActive: boolean;
}

// ============== Bulk Create DTOs ==============

export interface BulkCreateItemDto {
  stockCode: string;
  name: string;
  categoryId?: string | null;
  brandId?: string | null;
  quantity: number;
  purchasePrice?: number | null;
  purchaseCurrency?: string;
  salePrice: number;
  saleCurrency?: string;
  listPrice?: number | null;
  unit?: string;
  canBeNegative?: boolean;
  description?: string;
  barcode?: string;
  isDomesticProduct?: boolean;
  /** Ürün tartıya PLU olarak gönderilecek mi */
  isScale?: boolean;
  /** Ağırlık barkodunun ortasındaki 5 hane; baştaki sıfırlar anlamlı */
  scaleCode?: string;
  /**
   * Raf ömrü (gün). Tartı etikete son tüketim tarihini kendisi hesaplıyor:
   * tartım tarihi + gün - 1. Takvim tarihi değil, gün sayısı gönderiliyor.
   */
  shelfLifeDays?: number;
  countryOfOrigin?: string;
  vatRate?: number;
  vatStatus?: string;
  sctRate?: number | null;
  sctStatus?: string | null;
  imageUrl?: string;
  imageUrls?: string[];
  branchesId?: string | null;
  departmentNo?: number | null;
  isEcommerce?: boolean;
  isActive?: boolean;
}

export interface BulkCreateResultDto {
  totalCount: number;
  successCount: number;
  failedCount: number;
  failedItems: BulkCreateFailedItemDto[];
}

export interface BulkCreateFailedItemDto {
  stockCode: string;
  name: string;
  error: string;
}

export interface InventoryItemDto {
  id: string;
  companyId: string;
  stockCode: string;
  name: string;
  categoryId?: string;
  brandId?: string;
  // ❌ KALDIRILDI: warehouseId ve quantity artık WarehouseStockDto'da
  // warehouseId?: string;
  // quantity: number;
  // Fiyatlar
  purchasePrice?: number;
  purchaseCurrency?: string;
  purchaseVendor?: string;
  purchasePrice2?: number;
  purchaseCurrency2?: string;
  purchaseVendor2?: string;
  purchasePrice3?: number;
  purchaseCurrency3?: string;
  purchaseVendor3?: string;
  salePrice: number;
  saleCurrency: string;
  saleCustomer?: string;
  salePrice2?: number;
  saleCurrency2?: string;
  saleCustomer2?: string;
  salePrice3?: number;
  saleCurrency3?: string;
  saleCustomer3?: string;
  listPrice?: number;
  listCurrency?: string;
  // Stok
  unit: string;
  canBeNegative: boolean;
  minStockLevel?: number;
  maxStockLevel?: number;
  description?: string;
  barcode?: string;
  isDomesticProduct?: boolean;
  /** Ürün tartıya PLU olarak gönderilecek mi */
  isScale?: boolean;
  /** Ağırlık barkodunun ortasındaki 5 hane; baştaki sıfırlar anlamlı */
  scaleCode?: string;
  /**
   * Raf ömrü (gün). Tartı etikete son tüketim tarihini kendisi hesaplıyor:
   * tartım tarihi + gün - 1. Takvim tarihi değil, gün sayısı gönderiliyor.
   */
  shelfLifeDays?: number;
  countryOfOrigin?: string;
  desi?: number;
  vatRate: number;
  vatStatus: string;
  sctRate?: number;
  sctStatus?: string;
  imageUrl?: string; // ✅ Eklendi
  imageUrls?: string[]; // ✅ Eklendi
  variants?: any; // ✅ Eklendi
  branchesId?: string; // ✅ Eklendi
  parentItemId?: string | null;
  isVariantParent?: boolean;
  printerGroup?: string; // ✅ Eklendi
  departmentNo?: number; // ✅ Eklendi
  itemType?: string;
  isActive: boolean;
  isEcommerce?: boolean; // ✅ Eklendi
  createdAt: string;
  updatedAt?: string;
  // ✅ YENİ: Depo stokları (backend'den gelirse)
  warehouseStocks?: {
    id: string;
    productId: string;
    warehouseId: string;
    warehouseName?: string;
    quantity: number;
    createdAt: string;
    updatedAt?: string;
  }[];
}

export interface VariantGroupDto {
  id: string;
  companyId: string;
  groupName: string;
  description?: string;
  axis1Type?: string;
  axis2Type?: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface VariantValueDto {
  id: string;
  companyId: string;
  variantGroupId: string;
  valueType: string;
  valueName: string;
  valueCode?: string;
  sortOrder: number;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateVariantGroupDto {
  groupName: string;
  description?: string;
  axis1Type?: string;
  axis2Type?: string;
  isActive?: boolean;
}

export interface UpdateVariantGroupDto {
  id: string;
  groupName: string;
  description?: string;
  axis1Type?: string;
  axis2Type?: string;
  isActive: boolean;
}

export interface DeleteVariantGroupDto {
  id: string;
}

export interface CreateVariantValueDto {
  variantGroupId: string;
  valueType: string;
  valueName: string;
  valueCode?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface UpdateVariantValueDto {
  id: string;
  variantGroupId: string;
  valueType: string;
  valueName: string;
  valueCode?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface DeleteVariantValueDto {
  id: string;
}

export interface VariantSelectionDto {
  valueType: string;
  valueName: string;
}

export interface VariantCombinationCreateDto {
  values: VariantSelectionDto[];
  nameSuffix?: string;
  stockCodeSuffix?: string;
  quantity?: number;
  warehouseId?: string;
  imageUrl?: string;
  imageUrls?: string[];
}

export interface BulkCreateVariantsDto {
  parentItemId: string;
  variantGroupId?: string;
  appendValuesToName?: boolean;
  appendValuesToStockCode?: boolean;
  warehouseId?: string;
  quantity?: number;
  combinations: VariantCombinationCreateDto[];
}

export interface BulkCreateVariantsResultDto {
  totalCount: number;
  successCount: number;
  failedCount: number;
  createdItemIds: string[];
  failedItems: Array<{ stockCode: string; name: string; error: string }>;
}

/**
 * Tartıya gönderilebilecek ürünün senkron ekranına taşınan alanları.
 * Tam `InventoryItem` değil: liste bütün olarak bellekte tutulduğu için
 * yalnızca karşılaştırmada ve gönderimde kullanılanlar taşınıyor.
 */
export interface ScaleCandidate {
  id: string;
  name: string;
  stockCode?: string;
  barcode?: string;
  unit?: string;
  scaleCode?: string;
  isScale?: boolean;
  salePrice: number;
  vatRate: number;
  vatStatus?: string;
  shelfLifeDays?: number;
  categoryId?: string;
}

/**
 * Inventory SQL API CRUD işlemleri için service
 */
export class InventoryService {
  // ============== SQL API Methods ==============
  
  /**
   * Yeni ürün ekle (SQL API)
   */
  static async createItem(dto: CreateInventoryItemDto): Promise<InventoryItemDto> {
    console.log('🏷️ [INVENTORY SERVICE] createItem - Gönderilen itemType:', dto.itemType, '| DTO keys:', Object.keys(dto));
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_CREATE, dto);
    console.log('🏷️ [INVENTORY SERVICE] createItem - Dönen itemType:', response.data?.itemType);
    return response.data;
  }

  /**
   * Toplu ürün ekleme (Excel import için optimize edilmiş)
   * Frontend 100'er ürün gönderir, backend tek seferde kaydeder
   */
  static async bulkCreateItems(
    companyId: string,
    warehouseId: string,
    items: BulkCreateItemDto[]
  ): Promise<BulkCreateResultDto> {
    console.log(`📦 [BULK CREATE] Toplu ürün ekleme: ${items.length} ürün, warehouseId: ${warehouseId}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_CREATE, {
      companyId,
      warehouseId,
      items
    });
    
    console.log(`✅ [BULK CREATE] Sonuç:`, response.data);
    return response.data;
  }

  /**
   * Tüm ürünleri getir (SQL API)
   * @param companyId - Şirket ID'si
   * @param warehouseId - (Opsiyonel) Depo filtresi
   */
  static async getAllItemsSQL(companyId: string, warehouseId?: string | null): Promise<InventoryItemDto[]> {
    if (warehouseId) {
      console.log('📦 [INVENTORY SERVICE] Depo filtresi ile POST:', { warehouseId });
      // Depo filtresi varsa POST ile paged endpoint'i kullan
      const response = await accountingApi.post(
        `${ACCOUNTING_API.INVENTORY_ITEMS_PAGED}`,
        {
          pageNumber: 1,
          pageSize: 100000,
          warehouseId: warehouseId
        }
      );
      return response.data.items || [];
    } else {
      console.log('📦 [INVENTORY SERVICE] Depo filtresi YOK, GET kullanılıyor');
      // Depo filtresi yoksa normal GET endpoint
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEM_LIST);
      return response.data;
    }
  }

  /**
   * ✅ YENİ: Server-side pagination ile ürünleri sayfa sayfa çeker
   * @param companyId - Şirket ID'si
   * @param pageNumber - Sayfa numarası (1'den başlar)
   * @param pageSize - Sayfa başına ürün sayısı
   * @param searchTerm - Arama terimi (opsiyonel)
   * @param categoryId - Kategori filtresi (opsiyonel)
   * @param brandId - Marka filtresi (opsiyonel)
   * @param warehouseId - Depo filtresi (opsiyonel)
   * @param isActive - Aktiflik filtresi (opsiyonel)
   * @param isEcommerce - E-ticaret filtresi (opsiyonel)
   * @param sortNewestFirst - Sıralama yönü (opsiyonel) - true: yeniden eskiye (CreatedAt DESC), false: eskiden yeniye (CreatedAt ASC)
   * @returns Sayfalı ürün listesi ve toplam sayı
   */
  static async getPagedItemsSQL(
    companyId: string,
    pageNumber: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
    categoryId?: string | null,
    brandId?: string | null,
    warehouseId?: string | null,
    isActive?: boolean | null,
    isEcommerce?: boolean | null,
    sortNewestFirst?: boolean,
    hideZeroPrice?: boolean,
    vatRate?: number | null,
    departmentNo?: number | null,
    sortColumn?: string | null,
    sortDirection?: string | null,
    excludeVariantChildren: boolean = true,
    advanced?: InventoryAdvancedFilters
  ): Promise<{ items: InventoryItemDto[], totalCount: number, pageNumber: number, pageSize: number }> {
    // Canlı accounting API henüz minSalePrice / onlyWithoutImage vb. alanları
    // tanımıyorsa JSON'da yok sayılır. Bu durumda tüm uygun kayıtları çekip
    // gelişmiş filtreleri istemci tarafında uygularız (doğru totalCount + sayfalama).
    const needsClientAdvanced = hasClientSideAdvancedFilters(advanced);
    const requestPageNumber = needsClientAdvanced ? 1 : pageNumber;
    const requestPageSize = needsClientAdvanced ? 100000 : pageSize;

    console.log('📦 [INVENTORY SERVICE] Server-side pagination ile ürünler yükleniyor:', {
      pageNumber,
      pageSize,
      requestPageNumber,
      requestPageSize,
      needsClientAdvanced,
      searchTerm,
      categoryId,
      brandId,
      warehouseId,
      isActive,
      isEcommerce,
      sortNewestFirst,
      hideZeroPrice,
      vatRate,
      departmentNo,
      sortColumn,
      sortDirection,
      advanced
    });

    const response = await accountingApi.post(
      `${ACCOUNTING_API.INVENTORY_ITEMS_PAGED}`,
      {
        pageNumber: requestPageNumber,
        pageSize: requestPageSize,
        searchTerm: searchTerm || undefined,
        categoryId: categoryId || undefined,
        brandId: brandId || undefined,
        warehouseId: warehouseId || undefined,
        isActive: isActive !== null ? isActive : undefined,
        isEcommerce: isEcommerce !== null ? isEcommerce : undefined,
        sortNewestFirst: sortNewestFirst,
        hideZeroPrice: hideZeroPrice,
        vatRate: vatRate !== null ? vatRate : undefined,
        departmentNo: departmentNo !== null ? departmentNo : undefined,
        sortColumn: sortColumn || undefined,
        sortDirection: sortDirection || undefined,
        // Varyant child kayıtlarını ana listeden hariç tut.
        // "Ürün ve Stok" listesi default olarak true; reçete/menü modalları false override edebilir.
        excludeVariantChildren: excludeVariantChildren,
        // ✅ Gelişmiş filtreler (yeni API destekliyorsa sunucuda uygulanır)
        hideZeroStock: advanced?.hideZeroStock ? true : undefined,
        hidePositiveStock: advanced?.hidePositiveStock ? true : undefined,
        branchId: advanced?.branchId || undefined,
        locationId: advanced?.locationId || undefined,
        minSalePrice: advanced?.minSalePrice ?? undefined,
        maxSalePrice: advanced?.maxSalePrice ?? undefined,
        minQuantity: advanced?.minQuantity ?? undefined,
        maxQuantity: advanced?.maxQuantity ?? undefined,
        createdFrom: advanced?.createdFrom || undefined,
        createdTo: advanced?.createdTo || undefined,
        categoryIds: advanced?.categoryIds && advanced.categoryIds.length > 0 ? advanced.categoryIds : undefined,
        brandIds: advanced?.brandIds && advanced.brandIds.length > 0 ? advanced.brandIds : undefined,
        onlyWithoutImage: advanced?.onlyWithoutImage ? true : undefined
      }
    );

    // API'den gelen items'ı map et (PascalCase -> camelCase dönüşümü)
    let items = (response.data.items || []).map((item: any) => ({
      ...item,
      // UseBranchPricing PascalCase olarak geldiği için camelCase'e dönüştür
      useBranchPricing: item.useBranchPricing ?? item.UseBranchPricing ?? false,
      warehouseStocks: item.warehouseStocks ?? item.WarehouseStocks ?? [],
      imageUrl: item.imageUrl ?? item.ImageUrl ?? '',
      salePrice: item.salePrice ?? item.SalePrice ?? 0,
      createdAt: item.createdAt ?? item.CreatedAt,
      branchesId: item.branchesId ?? item.BranchesId ?? null,
      brandId: item.brandId ?? item.BrandId ?? null,
      categoryId: item.categoryId ?? item.CategoryId ?? null,
    }));

    if (needsClientAdvanced && advanced) {
      const before = items.length;
      items = applyAdvancedFiltersClientSide(items, advanced, warehouseId);
      console.log(`🔎 [INVENTORY SERVICE] İstemci gelişmiş filtre: ${before} → ${items.length}`, advanced);
      const totalCount = items.length;
      const start = Math.max(0, (pageNumber - 1) * pageSize);
      items = items.slice(start, start + pageSize);
      return {
        items,
        totalCount,
        pageNumber,
        pageSize
      };
    }

    return {
      items,
      totalCount: response.data.totalCount || 0,
      pageNumber: response.data.pageNumber || pageNumber,
      pageSize: response.data.pageSize || pageSize
    };
  }

  /**
   * Ürünü sil (SQL API - Soft Delete)
   */
  static async deleteItem(companyId: string, itemId: string, deletedBy?: string): Promise<void> {
    const deletedByParam = deletedBy || 'system';
    console.log(`🗑️ [DELETE SERVICE] Silme isteği gönderiliyor: itemId=${itemId}, deletedBy=${deletedByParam}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_DELETE(itemId), null, {
      params: { deletedBy: deletedByParam }
    });
    
    console.log('✅ [DELETE SERVICE] API Yanıtı:', response.data);
  }

  /**
   * Ürünü güncelle (SQL API)
   */
  static async updateItem(companyId: string, itemId: string, dto: any): Promise<InventoryItemDto> {
    console.log(`📝 [UPDATE SERVICE] Güncelleme isteği gönderiliyor: companyId=${companyId}, itemId=${itemId}`, dto);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_UPDATE(itemId), dto);
    
    console.log('✅ [UPDATE SERVICE] API Yanıtı:', response.data);
    return response.data;
  }

  static async updateDescription(itemId: string, description: string): Promise<InventoryItemDto> {
    const pick = (item: any, camel: string, pascal: string) =>
      item?.[camel] !== undefined ? item[camel] : item?.[pascal];

    // Canlı accounting API henüz /description route'unu yayınlamadı (404).
    // Mevcut GET + /update ile yalnızca açıklamayı günceller.
    const { data: item } = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEM_BY_ID(itemId));
    const payload = {
      id: pick(item, 'id', 'Id') || itemId,
      stockCode: pick(item, 'stockCode', 'StockCode') || '',
      name: pick(item, 'name', 'Name') || '',
      categoryId: pick(item, 'categoryId', 'CategoryId') ?? null,
      brandId: pick(item, 'brandId', 'BrandId') ?? null,
      purchasePrice: pick(item, 'purchasePrice', 'PurchasePrice') ?? null,
      purchaseCurrency: pick(item, 'purchaseCurrency', 'PurchaseCurrency') || 'TRY',
      purchaseVendor: pick(item, 'purchaseVendor', 'PurchaseVendor') ?? null,
      purchasePrice2: pick(item, 'purchasePrice2', 'PurchasePrice2') ?? null,
      purchaseCurrency2: pick(item, 'purchaseCurrency2', 'PurchaseCurrency2') || 'TRY',
      purchaseVendor2: pick(item, 'purchaseVendor2', 'PurchaseVendor2') ?? null,
      purchasePrice3: pick(item, 'purchasePrice3', 'PurchasePrice3') ?? null,
      purchaseCurrency3: pick(item, 'purchaseCurrency3', 'PurchaseCurrency3') || 'TRY',
      purchaseVendor3: pick(item, 'purchaseVendor3', 'PurchaseVendor3') ?? null,
      salePrice: pick(item, 'salePrice', 'SalePrice') ?? 0,
      saleCurrency: pick(item, 'saleCurrency', 'SaleCurrency') || 'TRY',
      saleCustomer: pick(item, 'saleCustomer', 'SaleCustomer') ?? null,
      salePrice2: pick(item, 'salePrice2', 'SalePrice2') ?? null,
      saleCurrency2: pick(item, 'saleCurrency2', 'SaleCurrency2') || 'TRY',
      saleCustomer2: pick(item, 'saleCustomer2', 'SaleCustomer2') ?? null,
      salePrice3: pick(item, 'salePrice3', 'SalePrice3') ?? null,
      saleCurrency3: pick(item, 'saleCurrency3', 'SaleCurrency3') || 'TRY',
      saleCustomer3: pick(item, 'saleCustomer3', 'SaleCustomer3') ?? null,
      listPrice: pick(item, 'listPrice', 'ListPrice') ?? null,
      listCurrency: pick(item, 'listCurrency', 'ListCurrency') || 'TRY',
      unit: pick(item, 'unit', 'Unit') || 'Adet',
      canBeNegative: pick(item, 'canBeNegative', 'CanBeNegative') ?? true,
      minStockLevel: pick(item, 'minStockLevel', 'MinStockLevel') ?? null,
      maxStockLevel: pick(item, 'maxStockLevel', 'MaxStockLevel') ?? null,
      description,
      barcode: pick(item, 'barcode', 'Barcode') ?? null,
      desi: pick(item, 'desi', 'Desi') ?? null,
      vatRate: pick(item, 'vatRate', 'VatRate') ?? 0,
      vatStatus: pick(item, 'vatStatus', 'VatStatus') || 'excluded',
      sctRate: pick(item, 'sctRate', 'SctRate') ?? null,
      sctStatus: pick(item, 'sctStatus', 'SctStatus') ?? null,
      imageUrl: pick(item, 'imageUrl', 'ImageUrl') ?? null,
      imageUrls: pick(item, 'imageUrls', 'ImageUrls') ?? null,
      variants: pick(item, 'variants', 'Variants') ?? null,
      parentItemId: pick(item, 'parentItemId', 'ParentItemId') ?? null,
      isVariantParent: pick(item, 'isVariantParent', 'IsVariantParent') ?? false,
      variantGroupId: pick(item, 'variantGroupId', 'VariantGroupId') ?? null,
      branchesId: pick(item, 'branchesId', 'BranchesId') ?? null,
      printerGroup: pick(item, 'printerGroup', 'PrinterGroup') ?? null,
      departmentNo: pick(item, 'departmentNo', 'DepartmentNo') ?? null,
      itemType: pick(item, 'itemType', 'ItemType') || 'PRODUCT',
      isActive: pick(item, 'isActive', 'IsActive') ?? true,
    };

    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_UPDATE(itemId), payload);
    return response.data;
  }

  /**
   * Ürün miktarını güncelle (SQL API)
   * ⚠️ YENİ SİSTEM: Artık warehouseId gerekli - quantity depo bazlı
   */
  static async updateQuantity(companyId: string, itemId: string, warehouseId: string, quantity: number): Promise<void> {
    console.log(`📦 [UPDATE QUANTITY] Miktar güncelleniyor: companyId=${companyId}, itemId=${itemId}, warehouseId=${warehouseId}, quantity=${quantity}`);
    
    await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_UPDATE_QUANTITY, {
      companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
      id: itemId,
      warehouseId: warehouseId, // ✅ Artık zorunlu
      quantity: quantity
    });
    
    console.log('✅ [UPDATE QUANTITY] Miktar başarıyla güncellendi');
  }

  /**
   * Ürün fiyatını güncelle (SQL API)
   */
  static async updatePrice(
    companyId: string, 
    itemId: string, 
    salePrice: number, 
    listPrice?: number,
    userId?: string,
    userName?: string
  ): Promise<void> {
    console.log(`💰 [UPDATE PRICE] Fiyat güncelleniyor: companyId=${companyId}, itemId=${itemId}, salePrice=${salePrice}, listPrice=${listPrice}`);
    console.log(`👤 [UPDATE PRICE] userId=${userId}, userName=${userName}`);
    
    // ⚠️ CRITICAL: Backend PascalCase bekliyor! (CRITICAL_API_DEVELOPMENT_RULES.md)
    const payload = {
      CompanyId: companyId,
      Id: itemId,
      SalePrice: salePrice,
      ListPrice: listPrice || null,
      UserId: userId || null,
      UserName: userName || null
    };
    console.log('📤 [UPDATE PRICE] Gönderilen payload:', JSON.stringify(payload, null, 2));
    
    await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_UPDATE_PRICE, payload);
    
    console.log('✅ [UPDATE PRICE] Fiyat başarıyla güncellendi');
  }

  /**
   * Toplu ürün silme (SQL API - Soft Delete)
   */
  static async bulkDeleteItems(companyId: string, itemIds: string[]): Promise<number> {
    console.log(`🗑️ [BULK DELETE] Toplu silme başlatıldı: companyId=${companyId}`, itemIds);
    console.log(`🗑️ [BULK DELETE] itemIds type:`, typeof itemIds, Array.isArray(itemIds));
    console.log(`🗑️ [BULK DELETE] itemIds.length:`, itemIds?.length);
    
    if (!itemIds || !Array.isArray(itemIds)) {
      throw new Error('itemIds parametresi bir array olmalı');
    }
    
    if (itemIds.length === 0) {
      console.log('⚠️ [BULK DELETE] Silinecek ürün yok');
      return 0;
    }
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_DELETE, {
      ids: itemIds
    });
    
    console.log(`✅ [BULK DELETE] ${response.data.count} ürün başarıyla silindi`);
    return response.data.count;
  }

  /**
   * Tartıya gönderilebilecek ürünler (birimi kg olan aktif ürünler).
   *
   * Senkron ekranı listeyi bütün olarak tutuyor: cihazdaki kayıtlarla
   * karşılaştırma sayfa sayfa yapılamaz. Bu yüzden uç sayfalamıyor, yalnızca
   * gereken alanları taşıyor.
   */
  static async getScaleCandidates(): Promise<ScaleCandidate[]> {
    const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEM_SCALE_CANDIDATES);
    const rows = Array.isArray(response.data) ? response.data : [];

    // API PascalCase döndürüyor.
    return rows.map((r: any) => ({
      id: r.id ?? r.Id,
      name: r.name ?? r.Name,
      stockCode: r.stockCode ?? r.StockCode,
      barcode: r.barcode ?? r.Barcode,
      unit: r.unit ?? r.Unit,
      scaleCode: r.scaleCode ?? r.ScaleCode,
      isScale: r.isScale ?? r.IsScale,
      salePrice: Number(r.salePrice ?? r.SalePrice) || 0,
      vatRate: Number(r.vatRate ?? r.VatRate) || 0,
      vatStatus: r.vatStatus ?? r.VatStatus,
      shelfLifeDays: r.shelfLifeDays ?? r.ShelfLifeDays,
      categoryId: r.categoryId ?? r.CategoryId,
    }));
  }

  /**
   * Tartı kodu boş olan ürünlere sıradaki kodları atatır.
   *
   * Kod hem ağırlık barkodunun ortasına basılıyor hem de tartıdaki PLU
   * numarası oluyor; şirket içinde benzersiz olmak zorunda. Bu yüzden numarayı
   * istemci üretmiyor, sunucu tek istekte atıyor.
   */
  static async assignScaleCodes(itemIds: string[]): Promise<{
    assigned: Array<{ id: string; name: string; scaleCode: string }>;
    skipped: Array<{ id: string; name: string; reason: string }>;
  }> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_ASSIGN_SCALE_CODES, {
      ids: itemIds,
    });

    // API PascalCase döndürüyor; alan adlarını iki durumda da karşılıyoruz.
    const raw = response.data || {};
    const list = (value: any): any[] => (Array.isArray(value) ? value : []);
    const norm = (row: any) => ({
      id: row.id ?? row.Id,
      name: row.name ?? row.Name,
      scaleCode: row.scaleCode ?? row.ScaleCode,
      reason: row.reason ?? row.Reason,
    });

    return {
      assigned: list(raw.assigned ?? raw.Assigned).map(norm),
      skipped: list(raw.skipped ?? raw.Skipped).map(norm),
    };
  }

  /**
   * Toplu ürün KALICI silme (hard delete — geri alınamaz).
   *
   * bulkDeleteItems yalnızca IsDeleted işaretler; bu uç kaydı veritabanından fiziksel
   * olarak siler. Ürünün stok hareketleri ve fiyat geçmişi de birlikte silinir.
   */
  static async bulkHardDeleteItems(companyId: string, itemIds: string[]): Promise<number> {
    if (!itemIds || !Array.isArray(itemIds)) {
      throw new Error('itemIds parametresi bir array olmalı');
    }

    if (itemIds.length === 0) {
      return 0;
    }

    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_HARD_DELETE, {
      ids: itemIds
    });

    console.log(`🗑️ [BULK HARD DELETE] ${response.data.count} ürün kalıcı olarak silindi`);
    return response.data.count ?? 0;
  }

  /**
   * Şirketin TÜM ürünlerinin id'lerini döner. Ekrandaki filtre ve depo seçimi
   * dikkate alınmaz — "tüm ürünleri sil" akışında hem sayıyı göstermek hem de
   * silinecek kümeyi sabitlemek için kullanılır.
   */
  static async getAllCompanyItemIds(companyId: string): Promise<string[]> {
    const allItems = await this.getAllItems(companyId);
    return allItems.map(item => item.id).filter(Boolean);
  }

  /**
   * Verilen ürünleri kalıcı olarak siler. Tek istekte binlerce kayıt göndermemek için
   * parçalara bölünür; her parça sonrası ilerleme bildirilir.
   */
  static async hardDeleteAllCompanyItems(
    companyId: string,
    itemIds: string[],
    onProgress?: (deleted: number, total: number) => void
  ): Promise<number> {
    const total = itemIds.length;

    if (total === 0) {
      onProgress?.(0, 0);
      return 0;
    }

    const chunkSize = 50;
    let deleted = 0;

    for (let i = 0; i < itemIds.length; i += chunkSize) {
      const chunk = itemIds.slice(i, i + chunkSize);
      deleted += await this.bulkHardDeleteItems(companyId, chunk);
      onProgress?.(deleted, total);
    }

    return deleted;
  }

  /**
   * Toplu ürün durumu güncelleme (SQL API)
   */
  static async bulkUpdateStatus(companyId: string, itemIds: string[], isActive: boolean): Promise<number> {
    console.log(`🔄 [BULK UPDATE STATUS] Toplu durum güncelleme: ${itemIds.length} ürün, isActive=${isActive}, CompanyId=${companyId}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_STATUS, {
      companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
      ids: itemIds,
      isActive: isActive
    });
    
    console.log(`✅ [BULK UPDATE STATUS] ${response.data.count} ürün durumu güncellendi`);
    return response.data.count;
  }

  /**
   * Toplu fiyat güncelleme (SQL API)
   */
  static async bulkUpdatePrice(
    companyId: string,
    itemIds: string[], 
    priceUpdateType: 'percentage' | 'fixed', 
    priceUpdateValue: number, 
    priceUpdateTarget: 'sale' | 'purchase'
  ): Promise<number> {
    console.log(`💰 [BULK UPDATE PRICE] Toplu fiyat güncelleme: ${itemIds.length} ürün, CompanyId=${companyId}`);
    console.log(`📊 [BULK UPDATE PRICE] Tip: ${priceUpdateType}, Değer: ${priceUpdateValue}, Hedef: ${priceUpdateTarget}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_PRICE, {
      companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
      ids: itemIds,
      priceUpdateType: priceUpdateType === 'percentage' ? 0 : 1,
      priceUpdateValue: priceUpdateValue,
      priceUpdateTarget: priceUpdateTarget
    });
    
    console.log(`✅ [BULK UPDATE PRICE] ${response.data.count} ürün fiyatı güncellendi`);
    return response.data.count;
  }

  /**
   * Toplu e-ticaret aç/kapat (SQL API)
   */
  static async bulkToggleEcommerce(
    companyId: string,
    itemIds: string[], 
    enable: boolean
  ): Promise<number> {
    console.log(`🛒 [BULK TOGGLE ECOMMERCE] Toplu e-ticaret: ${itemIds.length} ürün, ${enable ? 'aç' : 'kapat'}, CompanyId=${companyId}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_TOGGLE_ECOMMERCE, {
      companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
      ids: itemIds,
      enable: enable
    });
    
    console.log(`✅ [BULK TOGGLE ECOMMERCE] ${response.data.count} ürün güncellendi`);
    return response.data.count;
  }

  /**
   * Toplu depo atama - Deposuz ürünlere depo atar (SQL API)
   * @param companyId - Şirket ID
   * @param productIds - Ürün ID listesi
   * @param warehouseId - Hedef depo ID
   * @param initialQuantity - Başlangıç miktarı (varsayılan: 0)
   * @returns Atanan ürün sayısı ve depo ID
   */
  static async bulkAssignWarehouse(
    companyId: string,
    productIds: string[], 
    warehouseId: string,
    initialQuantity: number = 0
  ): Promise<{ count: number; warehouseId: string }> {
    console.log(`📦 [BULK ASSIGN WAREHOUSE] Toplu depo atama: ${productIds.length} ürün -> Depo: ${warehouseId}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_ASSIGN_WAREHOUSE, {
      productIds: productIds,
      warehouseId: warehouseId,
      companyId: companyId,
      initialQuantity: initialQuantity
    });
    
    console.log(`✅ [BULK ASSIGN WAREHOUSE] ${response.data.count} ürün depoya atandı`);
    return {
      count: response.data.count,
      warehouseId: response.data.warehouseId
    };
  }

  /**
   * Toplu KDV oranı güncelleme (SQL API)
   * @param itemIds - Ürün ID listesi
   * @param vatRate - Yeni KDV oranı (0-100)
   * @returns Güncellenen ürün sayısı
   */
  static async bulkUpdateVatRate(
    companyId: string,
    itemIds: string[], 
    vatRate: number
  ): Promise<number> {
    console.log(`📊 [BULK UPDATE VAT RATE] Toplu KDV oranı güncelleme: ${itemIds.length} ürün, KDV=%${vatRate}, CompanyId=${companyId}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_VAT_RATE, {
      companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
      ids: itemIds,
      vatRate: vatRate
    });
    
    console.log(`✅ [BULK UPDATE VAT RATE] ${response.data.count} ürün KDV oranı güncellendi`);
    return response.data.count;
  }

  /**
   * Toplu ÖKC (Departman No) güncelleme (SQL API)
   * @param companyId - Şirket ID'si
   * @param itemIds - Ürün ID listesi
   * @param departmentNo - Yeni departman numarası (1-100)
   * @returns Güncellenen ürün sayısı
   */
  static async bulkUpdateDepartmentNo(
    companyId: string,
    itemIds: string[], 
    departmentNo: number
  ): Promise<number> {
    console.log(`🔢 [BULK UPDATE DEPARTMENT NO] Toplu ÖKC güncelleme: ${itemIds.length} ürün, ÖKC=${departmentNo}, CompanyId=${companyId}`);
    
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_DEPARTMENT_NO, {
      companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
      ids: itemIds,
      departmentNo: departmentNo
    });
    
    console.log(`✅ [BULK UPDATE DEPARTMENT NO] ${response.data.count} ürün ÖKC güncellendi`);
    return response.data.count;
  }

  /**
   * Toplu KDV + ÖKC güncelleme (SQL API)
   * Her iki değeri de aynı anda günceller
   * @param itemIds - Ürün ID listesi
   * @param vatRate - Yeni KDV oranı (0-100)
   * @param departmentNo - Yeni departman numarası (1-100)
   * @returns Güncellenen ürün sayısı
   */
  static async bulkUpdateVatAndDepartment(
    itemIds: string[], 
    vatRate: number,
    departmentNo: number
  ): Promise<number> {
    console.log(`🔧 [BULK UPDATE VAT+DEPT] Toplu KDV+ÖKC güncelleme: ${itemIds.length} ürün, KDV=%${vatRate}, ÖKC=${departmentNo}`);
    
    // İki ayrı API çağrısı yapıyoruz (backend'de tek endpoint yoksa)
    // Önce KDV, sonra ÖKC
    const vatResponse = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_VAT_RATE, {
      ids: itemIds,
      vatRate: vatRate
    });
    
    const deptResponse = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_DEPARTMENT_NO, {
      ids: itemIds,
      departmentNo: departmentNo
    });
    
    console.log(`✅ [BULK UPDATE VAT+DEPT] ${itemIds.length} ürün KDV ve ÖKC güncellendi`);
    return itemIds.length;
  }

  /**
   * Depo stok miktarını güncelle (SQL API)
   * @param companyId - Şirket ID
   * @param productId - Ürün ID
   * @param warehouseId - Depo ID
   * @param newQuantity - Yeni miktar (mutlak değer)
   */
  static async updateWarehouseStockQuantity(
    companyId: string,
    productId: string,
    warehouseId: string,
    newQuantity: number,
    locationId?: string
  ): Promise<void> {
    console.log(`📦 [UPDATE WAREHOUSE STOCK] Depo stok güncelleme: CompanyId=${companyId}, Ürün=${productId}, Depo=${warehouseId}, Yeni Miktar=${newQuantity}`);
    
    // Backend'e QuantityChange gönderiyoruz (pozitif/negatif fark)
    // Ama biz yeni miktarı set etmek istiyoruz, bu yüzden önce mevcut miktarı almamız gerekiyor
    // Şimdilik direkt yeni miktarı gönderelim, backend'de logic'i düzeltiriz
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_WAREHOUSE_STOCK_UPDATE, {
      companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
      productId: productId,
      warehouseId: warehouseId,
      quantityChange: newQuantity, // absolute set
      locationId: locationId || undefined
    });
    
    console.log(`✅ [UPDATE WAREHOUSE STOCK] Depo stok güncellendi`);
  }

  /**
   * Manuel stok hareketi oluştur (Stok Giriş/Çıkış)
   * @param dto - Manuel stok hareketi bilgileri
   * @returns Hareket ID ve yeni miktar
   */
  static async createManualStockMovement(dto: {
    companyId: string;
    productId: string;
    warehouseStockId?: string;
    warehouseId: string;
    locationId?: string;
    direction: 'IN' | 'OUT';
    quantity: number;
    unitPrice: number;
    referenceNumber?: string;
    description?: string;
    date: string; // ISO format
    createdBy: string;
  }): Promise<{ movementId: string; newQuantity: number }> {
    console.log('📦 [MANUAL STOCK MOVEMENT] Manuel stok hareketi oluşturuluyor:', dto);
    
    const response = await accountingApi.post(ACCOUNTING_API.STOCK_MOVEMENT_CREATE_MANUAL, dto);
    
    console.log('✅ [MANUAL STOCK MOVEMENT] Stok hareketi kaydedildi:', response.data);
    return {
      movementId: response.data.movementId,
      newQuantity: response.data.newQuantity
    };
  }

  /**
   * Barkod kontrolü yap (SQL API)
   */
  static async checkBarcodeExists(companyId: string, barcode: string): Promise<InventoryItemDto | null> {
    try {
      const items = await this.getAllItemsSQL(companyId);
      const existingItem = items.find(item => item.barcode === barcode);
      return existingItem || null;
    } catch (error) {
      console.error('Barkod kontrolü hatası:', error);
      throw error;
    }
  }

  /**
   * Depoya ürün ekle (Yeni Mantık - Transfer mantığı ile ama kaynak depo yok)
   * ✅ YENİ: Ürün oluşturulduktan sonra depoya stok eklemek için
   * Transfer mantığını kullanır ama kaynak depo olmadan sadece hedef depoya ekleme yapar
   */
  static async addStockToWarehouse(dto: CreateWarehouseStockDto): Promise<void> {
    console.log(`📦 [ADD STOCK TO WAREHOUSE] Depoya ürün ekleniyor:`, dto);
    
    await accountingApi.post(ACCOUNTING_API.INVENTORY_WAREHOUSE_ADD_STOCK, dto);
    
    console.log('✅ [ADD STOCK TO WAREHOUSE] Depoya ürün başarıyla eklendi');
  }

  /**
   * Deponun tüm ürünlerini al (SQL API)
   */
  static async getWarehouseItems(
    companyId: string,
    warehouseId: string
  ): Promise<InventoryItem[]> {
    try {
      const response = await accountingApi.get(
        ACCOUNTING_API.INVENTORY_ITEMS + `?warehouseId=${warehouseId}`
      );
      return response.data || [];
    } catch (error) {
      console.error('❌ [SQL API] Depo ürünleri alınamadı:', error);
      return [];
    }
  }

  /**
   * Toplu silme (warehouse bazlı veya tümü) - basit versiyon
   * SQL API ile ürünleri çeker ve toplu siler
   */
  static async deleteAllItems(
    companyId: string,
    warehouseId?: string,
    onProgress?: (deleted: number, total: number) => void
  ): Promise<void> {
    // SQL'den silinecek ürünleri al (sadece ID'ler için)
    const itemsToDelete = warehouseId 
      ? await this.getWarehouseItems(companyId, warehouseId)
      : await this.getAllItems(companyId);
    
    const itemIds = itemsToDelete.map(item => item.id);
    const total = itemIds.length;
    
    if (itemIds.length === 0) {
      return;
    }
    
    // SQL'de toplu sil (chunk'lara bölerek)
    const chunkSize = 50;
    let deleted = 0;
    
    for (let i = 0; i < itemIds.length; i += chunkSize) {
      const chunk = itemIds.slice(i, i + chunkSize);
      
      await this.bulkDeleteItems(companyId, chunk); // ✅ companyId eklendi
      
      deleted += chunk.length;
      
      if (onProgress) {
        onProgress(deleted, total);
      }
      
      // Kısa bekleme (rate limiting için)
      if (i + chunkSize < itemIds.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  }

  /**
   * Tüm ürünleri al (SQL API)
   */
  static async getAllItems(companyId: string): Promise<InventoryItem[]> {
    try {
      const response = await accountingApi.get(
        ACCOUNTING_API.INVENTORY_ITEMS
      );
      return response.data || [];
    } catch (error) {
      console.error('❌ [SQL API] Tüm ürünler alınamadı:', error);
      return [];
    }
  }

  /**
   * QR Menü durumunu değiştir (SQL API - V2)
   */
  static async toggleQRMenuStatus(
    companyId: string,
    itemId: string,
    enable: boolean
  ): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      console.log(`🍽️ [INVENTORY SERVICE] QR Menü durumu değiştiriliyor: ${itemId}, Enable: ${enable}`);
      
      const response = await accountingApi.post(
        `${ACCOUNTINGAPI_URL}/api/Inventory/${itemId}/toggle-qrmenu?companyId=${companyId}`,
        { enable },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ [INVENTORY SERVICE] QR Menü durumu başarıyla değiştirildi:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [INVENTORY SERVICE] QR Menü durumu değiştirme hatası:', error);
      
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'QR Menü durumu değiştirilemedi');
      }
      throw error;
    }
  }

  /**
   * Kiosk durumunu değiştir (SQL API - V2)
   */
  static async toggleKioskStatus(
    companyId: string,
    itemId: string,
    enable: boolean
  ): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      console.log(`🖥️ [INVENTORY SERVICE] Kiosk durumu değiştiriliyor: ${itemId}, Enable: ${enable}`);
      
      const response = await accountingApi.post(
        `${ACCOUNTINGAPI_URL}/api/Inventory/${itemId}/toggle-kiosk?companyId=${companyId}`,
        { enable },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ [INVENTORY SERVICE] Kiosk durumu başarıyla değiştirildi:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [INVENTORY SERVICE] Kiosk durumu değiştirme hatası:', error);
      
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Kiosk durumu değiştirilemedi');
      }
      throw error;
    }
  }

  /**
   * E-ticaret durumunu değiştir (SQL API)
   */
  static async toggleEcommerce(
    companyId: string,
    itemId: string,
    isEcommerce: boolean
  ): Promise<void> {
    // SQL API ile e-ticaret durumunu güncelle
    await this.bulkToggleEcommerce(companyId, [itemId], isEcommerce);
  }

  // ============== Branch Price Methods (Şube Bazlı Fiyatlandırma) ==============

  /**
   * Ürüne ait tüm şube fiyatlarını getir
   * ⚠️ GÜVENLİK: companyId zorunlu - başka şirketin verisine erişimi engeller
   */
  static async getBranchPrices(companyId: string, inventoryItemId: string): Promise<BranchPrice[]> {
    try {
      console.log(`📊 [BRANCH PRICE] Şube fiyatları getiriliyor: ${inventoryItemId}, Company: ${companyId}`);
      const response = await accountingApi.get(`${ACCOUNTINGAPI_URL}/api/InventoryBranchPrice/item/${inventoryItemId}`);
      console.log(`✅ [BRANCH PRICE] ${response.data.length} şube fiyatı bulundu`);
      return response.data;
    } catch (error: any) {
      // 404 = endpoint yok veya kayıt yok - boş array dön (hata değil)
      if (error.response?.status === 404) {
        console.log(`ℹ️ [BRANCH PRICE] Şube fiyatı bulunamadı veya API henüz aktif değil`);
        return [];
      }
      console.error('❌ [BRANCH PRICE] Şube fiyatları alınırken hata:', error);
      throw error;
    }
  }

  /**
   * Yeni şube fiyatı oluştur
   */
  static async createBranchPrice(dto: CreateBranchPriceDto): Promise<BranchPrice> {
    console.log(`📝 [BRANCH PRICE] Şube fiyatı oluşturuluyor:`, dto);
    const response = await accountingApi.post(`${ACCOUNTINGAPI_URL}/api/InventoryBranchPrice`, dto);
    console.log(`✅ [BRANCH PRICE] Şube fiyatı oluşturuldu:`, response.data);
    return response.data;
  }

  /**
   * Şube fiyatını güncelle
   * ⚠️ GÜVENLİK: companyId zorunlu - başka şirketin verisini değiştirmeyi engeller
   * ⚠️ CRITICAL: Backend PascalCase bekliyor! (CRITICAL_API_DEVELOPMENT_RULES.md)
   */
  static async updateBranchPrice(companyId: string, id: string, dto: UpdateBranchPriceDto): Promise<BranchPrice> {
    console.log(`📝 [BRANCH PRICE] Şube fiyatı güncelleniyor: ${id}, Company: ${companyId}`, dto);
    
    // Backend PascalCase bekliyor - dönüştür
    const payload = {
      Id: dto.id,
      CompanyId: dto.companyId,
      SalePrice: dto.salePrice,
      SaleCurrency: dto.saleCurrency || 'TRY',
      ListPrice: dto.listPrice || null,
      ListCurrency: dto.listCurrency || null,
      PackagePrice: dto.packagePrice || null,
      IsPurchaseOnly: dto.isPurchaseOnly ?? null,
      UserId: dto.userId || null,
      UserName: dto.userName || null,
      BranchId: dto.branchId || null,
      BranchName: dto.branchName || null
    };
    
    const response = await accountingApi.post(`${ACCOUNTINGAPI_URL}/api/InventoryBranchPrice/update/${id}`, payload);
    console.log(`✅ [BRANCH PRICE] Şube fiyatı güncellendi:`, response.data);
    return response.data;
  }

  /**
   * Şube fiyatını sil
   * ⚠️ GÜVENLİK: companyId zorunlu - başka şirketin verisini silmeyi engeller
   */
  static async deleteBranchPrice(companyId: string, id: string): Promise<void> {
    console.log(`🗑️ [BRANCH PRICE] Şube fiyatı siliniyor: ${id}, Company: ${companyId}`);
    await accountingApi.post(`${ACCOUNTINGAPI_URL}/api/InventoryBranchPrice/delete/${id}`);
    console.log(`✅ [BRANCH PRICE] Şube fiyatı silindi`);
  }

  /**
   * Ürünün UseBranchPricing değerini güncelle
   */
  static async toggleUseBranchPricing(
    companyId: string,
    inventoryItemId: string,
    useBranchPricing: boolean
  ): Promise<void> {
    console.log(`🔄 [BRANCH PRICE] UseBranchPricing güncelleniyor: ${inventoryItemId} -> ${useBranchPricing}`);
    await accountingApi.post(`${ACCOUNTINGAPI_URL}/api/InventoryBranchPrice/toggle-use-branch-pricing`, {
      companyId,
      inventoryItemId,
      useBranchPricing
    });
    console.log(`✅ [BRANCH PRICE] UseBranchPricing güncellendi`);
  }

  // ========== QUICK SALE ŞUBE FİYATLARI ==========

  /**
   * Birden fazla ürün için şube fiyatlarını toplu getir (Quick Sale için optimize edilmiş)
   * @param companyId Şirket ID
   * @param branchId Şube ID
   * @param inventoryItemIds Ürün ID listesi
   * @returns Dictionary: { [inventoryItemId]: BranchPrice }
   */
  static async getBulkBranchPrices(
    companyId: string,
    branchId: string,
    inventoryItemIds: string[]
  ): Promise<{ [inventoryItemId: string]: BranchPrice }> {
    try {
      if (!companyId || !branchId || inventoryItemIds.length === 0) {
        return {};
      }

      console.log(`📊 [BRANCH PRICE] Toplu şube fiyatları getiriliyor: ${inventoryItemIds.length} ürün, şube: ${branchId}`);
      
      const response = await accountingApi.post(`${ACCOUNTINGAPI_URL}/api/InventoryBranchPrice/bulk-by-branch`, {
        companyId,
        branchId,
        inventoryItemIds
      });
      
      const result = response.data || {};
      console.log(`✅ [BRANCH PRICE] ${Object.keys(result).length} şube fiyatı bulundu`);
      return result;
    } catch (error: any) {
      // 404 veya hata durumunda boş döndür (şube fiyatı yok demek)
      if (error.response?.status === 404) {
        console.log(`ℹ️ [BRANCH PRICE] Şube fiyatları bulunamadı (404)`);
        return {};
      }
      console.error(`❌ [BRANCH PRICE] Toplu şube fiyatları alınırken hata:`, error);
      return {};
    }
  }

  // ============== FİYAT DEĞİŞİKLİK LOGLARI ==============

  /**
   * Fiyat değişiklik loglarını getir - Server-side pagination ve filtreleme
   * inventoryItemId boşsa tüm loglar, doluysa sadece o ürünün logları getirilir
   *
   * ⚠️ GÜVENLİK: şirket izolasyonu backend'de X-Company-Id header'ından yapılır
   * (companyId parametresi yalnızca log/teşhis amaçlı; URL'e eklenmez).
   */
  static async getPriceChangeLogs(
    companyId: string, 
    inventoryItemId: string = '',
    page: number = 1,
    pageSize: number = 50,
    filters?: {
      searchTerm?: string;
      priceType?: string;
      startDate?: Date | null;
      endDate?: Date | null;
      sortColumn?: string;
      sortDirection?: 'asc' | 'desc';
    }
  ): Promise<PriceChangeLogResponse> {
    try {
      console.log(`📋 [PRICE LOGS] Fiyat logları getiriliyor: companyId=${companyId}, itemId=${inventoryItemId || 'ALL'}, page=${page}, filters=`, filters);
      
      // inventoryItemId boşsa tüm loglar, doluysa ürün bazlı.
      // companyId URL'e KOYULMAZ: backend şirketi X-Company-Id header'ından
      // çözüyor ve tek route parametresi `{inventoryItemId:Guid}`. companyId
      // path'e eklendiğinde Guid binding'i patlıyor ve 400 dönüyordu.
      const url = inventoryItemId 
        ? `${ACCOUNTINGAPI_URL}/api/Inventory/price-change-logs/${inventoryItemId}`
        : `${ACCOUNTINGAPI_URL}/api/Inventory/price-change-logs`;
      
      // Query parametrelerini hazırla
      const params: any = { page, pageSize };
      
      if (filters) {
        if (filters.searchTerm) params.searchTerm = filters.searchTerm;
        if (filters.priceType && filters.priceType !== 'all') params.priceType = filters.priceType;
        // Loglar veritabanında TR saatiyle duruyor; toISOString UTC'ye çevirip
        // sınırları 3 saat kaydırırdı (bkz. utils/dateUtils.toLocalIso)
        if (filters.startDate) params.startDate = toLocalIso(filters.startDate);
        if (filters.endDate) params.endDate = toLocalIso(filters.endDate);
        if (filters.sortColumn) params.sortColumn = filters.sortColumn;
        if (filters.sortDirection) params.sortDirection = filters.sortDirection;
      }
      
      const response = await accountingApi.get(url, { params });
      
      // Backend PascalCase döndürüyor, camelCase'e dönüştür
      const data = response.data;
      const logs = (data.data || data.Data || []).map((log: any) => ({
        id: log.Id || log.id,
        companyId: log.CompanyId || log.companyId,
        inventoryItemId: log.InventoryItemId || log.inventoryItemId,
        productName: log.ProductName || log.productName,
        stockCode: log.StockCode || log.stockCode,
        branchId: log.BranchId || log.branchId,
        branchName: log.BranchName || log.branchName,
        priceType: log.PriceType || log.priceType,
        oldValue: log.OldValue || log.oldValue,
        newValue: log.NewValue || log.newValue,
        currency: log.Currency || log.currency || 'TRY',
        userId: log.UserId || log.userId,
        userName: log.UserName || log.userName,
        source: log.Source || log.source,
        notes: log.Notes || log.notes,
        createdAt: log.CreatedAt || log.createdAt
      }));
      
      console.log(`✅ [PRICE LOGS] ${logs.length} log bulundu (Toplam: ${data.totalCount || data.TotalCount})`);

      const rawAgg = data.aggregates || data.Aggregates;
      const aggregates = rawAgg
        ? {
            increaseCount: rawAgg.increaseCount ?? rawAgg.IncreaseCount ?? 0,
            decreaseCount: rawAgg.decreaseCount ?? rawAgg.DecreaseCount ?? 0,
            averageChangePercent: rawAgg.averageChangePercent ?? rawAgg.AverageChangePercent ?? 0,
          }
        : undefined;

      return {
        data: logs,
        totalCount: data.totalCount || data.TotalCount || logs.length,
        page: data.page || data.Page || page,
        pageSize: data.pageSize || data.PageSize || pageSize,
        aggregates
      };
    } catch (error: any) {
      console.error(`❌ [PRICE LOGS] Fiyat logları alınırken hata:`, error);
      throw error;
    }
  }

  static async getProductLocations(productId: string, warehouseId: string): Promise<{ locationId: string; locationCode: string; locationFullPath: string; quantity: number }[]> {
    const resp = await accountingApi.get(ACCOUNTING_API.INVENTORY_PRODUCT_LOCATIONS(productId, warehouseId));
    return resp.data?.data ?? [];
  }

  static async getStockByLocation(warehouseId: string, params?: { locationId?: string; page?: number; pageSize?: number }) {
    const resp = await accountingApi.get(ACCOUNTING_API.INVENTORY_STOCK_BY_LOCATION(warehouseId), { params });
    return resp.data;
  }

  static async getVariantGroups(): Promise<VariantGroupDto[]> {
    const response = await accountingApi.get('/api/Inventory/variant-groups');
    return response.data || [];
  }

  static async getVariantValuesByGroup(groupId: string, valueType?: string): Promise<VariantValueDto[]> {
    const response = await accountingApi.get(`/api/Inventory/variant-groups/${groupId}/values`, {
      params: valueType ? { valueType } : undefined
    });
    return response.data || [];
  }

  static async getVariantsByParent(parentId: string): Promise<InventoryItemDto[]> {
    const response = await accountingApi.get(`/api/Inventory/items/${parentId}/variants`);
    return response.data || [];
  }

  static async bulkCreateVariants(dto: BulkCreateVariantsDto): Promise<BulkCreateVariantsResultDto> {
    const response = await accountingApi.post(
      `/api/Inventory/items/${dto.parentItemId}/variants/bulk-create`,
      dto
    );
    return response.data;
  }

  static async createVariantGroup(dto: CreateVariantGroupDto): Promise<VariantGroupDto> {
    const response = await accountingApi.post('/api/Inventory/variant-groups/create', dto);
    return response.data;
  }

  static async updateVariantGroup(dto: UpdateVariantGroupDto): Promise<VariantGroupDto> {
    const response = await accountingApi.post('/api/Inventory/variant-groups/update', dto);
    return response.data;
  }

  static async deleteVariantGroup(dto: DeleteVariantGroupDto): Promise<void> {
    await accountingApi.post('/api/Inventory/variant-groups/delete', dto);
  }

  static async createVariantValue(dto: CreateVariantValueDto): Promise<VariantValueDto> {
    const response = await accountingApi.post('/api/Inventory/variant-values/create', dto);
    return response.data;
  }

  static async updateVariantValue(dto: UpdateVariantValueDto): Promise<VariantValueDto> {
    const response = await accountingApi.post('/api/Inventory/variant-values/update', dto);
    return response.data;
  }

  static async deleteVariantValue(dto: DeleteVariantValueDto): Promise<void> {
    await accountingApi.post('/api/Inventory/variant-values/delete', dto);
  }
}

